// @ts-check
// Login sem senha (/welcome): e-mail, depois o código de 6 dígitos enviado para ele. No endereço da escola o código
// certo abre a sessão; no portal (app.) ele devolve as instituições da pessoa e o navegador segue para a escolhida.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { fetchPublicTenant, requestSignInEmailCode, verifySignInEmailCode } from '../../data/rest.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { MainButton } from '../../ui/button.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { Link } from '../../app/router.js';
import { AuthLayout, AuthTextField } from './auth-layout.js';
import { EMAIL_CODE_LENGTH, EmailCodeField, RESEND_COOLDOWN_SECONDS, ResendCodeButton, useCooldownSeconds } from './email-code-field.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ onSignedIn: () => void }} props
 */
export function SignInPage({ onSignedIn }) {
  const [tenant, setTenant] = useState(/** @type {{ displayName?: string, slug?: string } | null | undefined} */ (undefined));
  const [step, setStep] = useState(/** @type {'email' | 'code' | 'institutions'} */ ('email'));
  const [institutions, setInstitutions] = useState(/** @type {Array<{ slug: string, displayName: string, logoUrl?: string | null, redirectUrl: string }>} */ ([]));
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isDevCode, setIsDevCode] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [codeError, setCodeError] = useState(/** @type {string | null} */ (null));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(/** @type {number | null} */ (null));
  const resendSecondsLeft = useCooldownSeconds(resendAvailableAt);

  useEffect(() => {
    document.title = t('auth.signIn.documentTitle');
    fetchPublicTenant()
      .then((body) => setTenant(body?.data ?? body))
      .catch(() => setTenant(null));
  }, []);

  /** @param {{ sent?: boolean, devCode?: string }} response */
  const applyCodeResponse = (response) => {
    setResendAvailableAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
    if (response?.devCode) {
      setCode(response.devCode);
      setIsDevCode(true);
    }
  };

  const sendCode = async (/** @type {Event} */ event) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await requestSignInEmailCode({ email: email.trim() });
      setCode('');
      setIsDevCode(false);
      setCodeError(null);
      applyCodeResponse(response);
      setStep('code');
    } catch (sendError) {
      setError(getErrorMessage(sendError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendCode = async () => {
    setError(null);
    setCodeError(null);
    setIsResending(true);
    try {
      const response = await requestSignInEmailCode({ email: email.trim() });
      applyCodeResponse(response);
      showSuccessSnackbar(t('auth.emailCode.resent', { email: email.trim() }));
    } catch (sendError) {
      setError(getErrorMessage(sendError));
    } finally {
      setIsResending(false);
    }
  };

  /** @param {string} value */
  const verifyCode = async (value) => {
    if (value.length !== EMAIL_CODE_LENGTH || isSubmitting) return;
    setError(null);
    setCodeError(null);
    setIsSubmitting(true);
    try {
      const response = await verifySignInEmailCode({ email: email.trim(), code: value });
      const found = Array.isArray(response?.institutions) ? response.institutions : null;
      if (!found) {
        onSignedIn();
        return;
      }
      if (found.length === 1) {
        window.location.assign(found[0].redirectUrl);
        return;
      }
      setInstitutions(found);
      setStep('institutions');
    } catch (verifyError) {
      if (verifyError instanceof ApiError && verifyError.code === 'INVALID_EMAIL_CODE') {
        setCodeError(getErrorMessage(verifyError));
        setCode('');
        setIsDevCode(false);
      } else {
        setError(getErrorMessage(verifyError));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const changeEmail = () => {
    setStep('email');
    setCode('');
    setIsDevCode(false);
    setCodeError(null);
    setError(null);
  };

  // cadastrar instituição só existe no portal; no endereço da escola o link levaria a um erro
  const footer = tenant === null
    ? html`<span>${t('auth.signIn.noAccount')} <${Link} href="/sign-up">${t('auth.signIn.createInstitution')}<//></span>`
    : null;

  if (step === 'institutions') {
    return html`<${AuthLayout} title=${t('auth.signIn.chooseInstitutionTitle')} subtitle=${t('auth.signIn.chooseInstitutionSubtitle')} tenant=${tenant}>
      <div class="auth-institution-list" role="list">
        ${institutions.map((institution) => html`<a key=${institution.slug} role="listitem" class="auth-institution" href=${institution.redirectUrl}>
          ${institution.logoUrl
            ? html`<img class="auth-institution-initial" src=${institution.logoUrl} alt="" />`
            : html`<span class="auth-institution-initial" aria-hidden="true">${institution.displayName.slice(0, 1).toUpperCase()}</span>`}
          <span class="auth-institution-texts">
            <span class="auth-institution-name">${institution.displayName}</span>
            <span class="auth-institution-address">${institution.slug}.${window.location.hostname.replace(/^app\./, '')}</span>
          </span>
        </a>`)}
      </div>
      <div class="auth-secondary-actions">
        <button type="button" class="auth-link-button" onClick=${changeEmail}>${t('auth.emailCode.changeEmail')}</button>
      </div>
    <//>`;
  }

  if (step === 'code') {
    return html`<${AuthLayout}
      title=${t('auth.signIn.codeTitle')}
      subtitle=${html`${t('auth.signIn.codeSubtitle')} <strong class="auth-subtitle-strong">${email.trim()}</strong>`}
      tenant=${tenant}
      footer=${footer}
    >
      <form class="auth-form" onSubmit=${(/** @type {Event} */ event) => {
        event.preventDefault();
        verifyCode(code);
      }}>
        <${EmailCodeField}
          value=${code}
          onInput=${(value) => {
            setCode(value);
            setIsDevCode(false);
            if (codeError) setCodeError(null);
          }}
          onComplete=${verifyCode}
          error=${codeError}
          hint=${isDevCode ? t('auth.emailCode.devCodeHint') : t('auth.emailCode.hint')}
          disabled=${isSubmitting}
        />
        ${error ? html`<div class="auth-error" role="alert">${error}</div>` : null}
        <${MainButton} type="submit" title=${t('auth.signIn.submit')} isLoading=${isSubmitting} disabled=${code.length !== EMAIL_CODE_LENGTH} />
        <div class="auth-secondary-actions">
          <${ResendCodeButton} secondsLeft=${resendSecondsLeft} isSending=${isResending} onResend=${resendCode} />
          <button type="button" class="auth-link-button" onClick=${changeEmail}>${t('auth.emailCode.changeEmail')}</button>
        </div>
      </form>
    <//>`;
  }

  const title = tenant?.displayName ? t('auth.signIn.titleWithName', { name: tenant.displayName }) : t('auth.signIn.title');
  return html`<${AuthLayout}
    title=${title}
    subtitle=${tenant === null ? t('auth.signIn.portalHint') : t('auth.signIn.subtitle')}
    tenant=${tenant}
    footer=${footer}
  >
    <form class="auth-form" onSubmit=${sendCode}>
      <${AuthTextField} label=${t('auth.fields.email')} name="email" type="email" value=${email} onInput=${setEmail} placeholder=${t('auth.fields.emailPlaceholder')} autoComplete="email" autoFocus=${true} />
      ${error ? html`<div class="auth-error" role="alert">${error}</div>` : null}
      <${MainButton} type="submit" title=${t('auth.signIn.sendCode')} isLoading=${isSubmitting} disabled=${!email.trim()} />
    </form>
  <//>`;
}
